import { Well } from '../types';

export interface SnapshotFileEntry {
  url: string;
  size: number;
  sha256: string;
  facilityType: 'wells' | 'swds' | 'rigs' | 'facilities';
  label?: string;
}

export interface SnapshotManifest {
  version: string;
  generatedAt: string;
  files: SnapshotFileEntry[];
}

function getBaseUrl(): string {
  const envUrl = (import.meta as any).env?.VITE_SNAPSHOT_BASE_URL;
  if (envUrl) return envUrl.replace(/\/$/, '');
  return 'https://snapshots.ziawellnav.pages.dev'; // default Pages-bound R2 CDN
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { cache: 'no-cache' });
  if (!res.ok) throw new Error(`Snapshot fetch failed ${res.status} for ${url}`);
  return res.json();
}

async function fetchArrayBuffer(url: string): Promise<ArrayBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Snapshot download failed ${res.status} for ${url}`);
  const encoding = res.headers.get('content-encoding');
  const isGzip = encoding === 'gzip' || url.endsWith('.gz');

  // Browsers usually auto-decompress when content-encoding is set; this fallback handles raw gzip bodies
  if (isGzip && typeof DecompressionStream !== 'undefined' && res.body) {
    try {
      const stream = res.body.pipeThrough(new DecompressionStream('gzip'));
      const reader = stream.getReader();
      const chunks: Uint8Array[] = [];
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value) chunks.push(value);
      }
      const total = chunks.reduce((acc, cur) => acc + cur.byteLength, 0);
      const merged = new Uint8Array(total);
      let offset = 0;
      for (const chunk of chunks) {
        merged.set(chunk, offset);
        offset += chunk.byteLength;
      }
      return merged.buffer;
    } catch (err) {
      console.warn('DecompressionStream failed, falling back to arrayBuffer()', err);
    }
  }

  return res.arrayBuffer();
}

async function sha256Hex(buffer: ArrayBuffer): Promise<string> {
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

async function parseSnapshot(buffer: ArrayBuffer): Promise<Well[]> {
  const text = new TextDecoder().decode(buffer);
  return JSON.parse(text) as Well[];
}

export const snapshotService = {
  getBaseUrl,

  async fetchManifest(baseUrl?: string): Promise<SnapshotManifest> {
    const url = `${baseUrl || getBaseUrl()}/manifest.json`;
    return fetchJson<SnapshotManifest>(url);
  },

  async downloadSnapshot(entry: SnapshotFileEntry): Promise<Well[]> {
    const buffer = await fetchArrayBuffer(entry.url.startsWith('http') ? entry.url : `${getBaseUrl()}/${entry.url}`);
    const actual = await sha256Hex(buffer);
    if (entry.sha256 && actual !== entry.sha256.toLowerCase()) {
      throw new Error(`Checksum mismatch for ${entry.url}: expected ${entry.sha256}, got ${actual}`);
    }
    return parseSnapshot(buffer);
  }
};
